import React from "react";
import type { ReactNode } from "react";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

export type Money = number | undefined;

export type MoneySection = {
  salaryCommissionBonus?: Money;
  rentalIncome?: Money;
  personalAndFamilyExpense?: Money;
  mortgageLoanRepayment?: Money;
  insuranceContribution?: Money;
  hasOtherIncome?: boolean;
  otherIncomeName?: string;
  otherIncomeAmount?: Money;
  hasOtherExpense?: boolean;
  otherExpenseName?: string;
  otherExpenseAmount?: Money;
};

export type AssetsSection = {
  cashAndDeposits?: Money;
  liquidInvestments?: Money;
  hasOtherCurrentAssets?: boolean;
  otherCurrentAssetsName?: string;
  otherCurrentAssetsAmount?: Money;
  property?: Money;
  otherNonCurrentAssets?: Money;
};

export type LiabilitiesSection = {
  mortgageLoan?: Money;
  personalCreditCardLoan?: Money;
  hasOtherLiabilities?: boolean;
  otherLiabilityName?: string;
  otherLiabilityAmount?: Money;
};

export type ProtectionSection = {
  lifeInsurance?: string;
  criticalIllnessInsurance?: string;
  medicalInsuranceType?: string;
  medicalInsuranceAmount?: Money;
  accidentInsurance?: string;
  otherExistingProtection?: string;
  attachmentRemarks?: string;
};

export type FinancialSnapshot = {
  income?: MoneySection;
  expense?: MoneySection;
  currentAssets?: AssetsSection;
  nonCurrentAssets?: AssetsSection;
  liabilities?: LiabilitiesSection;
  protection?: ProtectionSection;
};

export type QuestionKind = "number" | "text" | "switch" | "select";

export type Predicate = { section: keyof FinancialSnapshot; key: string; equals?: any; truthy?: boolean } | { fn: (draft: FinancialSnapshot) => boolean };

export type ConditionGroup = {
  mode?: "all" | "any"; // default all
  predicates: Predicate[];
};

export type Question = {
  key: string;
  label: string;
  kind: QuestionKind;
  section: keyof FinancialSnapshot;
  colSpan?: number;
  variant?: "inlineSwitch"; // for special layouts like full-width switch row
  checkedChildren?: ReactNode;
  unCheckedChildren?: ReactNode;
  switchStyle?: React.CSSProperties;
  options?: Array<{ label: string; value: string | number | boolean }>;
  metadataCode?: string; // for select type: fetch options from global metadata
  visibleWhen?: ConditionGroup | ConditionGroup[];
  requiredWhen?: ConditionGroup | ConditionGroup[];
  disabledWhen?: ConditionGroup | ConditionGroup[];
  placeholder?: string;
};

export type GroupSummaryKey = "totalIncome" | "totalExpense" | "totalCurrentAssets" | "totalNonCurrentAssets" | "totalLiabilities";

export type SectionSummaryKey = "surplusDeficit" | "netAssets";

export type Group = {
  key: string;
  title?: string;
  gutter?: [number, number] | number[];
  summaryKey?: GroupSummaryKey;
  summaryValueSuffix?: string;
  summaryLabel?: string;
  questions: Question[];
};

export type Block = {
  key: string;
  headerSummaryKey?: SectionSummaryKey;
  headerLabel?: string;
  headerPrefix?: string;
  headerValueSuffix?: string;
  summaryLabelPrefix?: string;
  groups: Group[];
};

export type Section = {
  title?: string;
  blocks: Block[];
};

export const basicFnaConfig: Section[] = [
  {
    title: "一、財務資料",
    blocks: [
      {
        key: "financial",
        headerSummaryKey: "surplusDeficit",
        headerLabel: "1. 估計每月盈餘/超支 (HK$/港幣)",
        headerPrefix: "A-B = ",
        headerValueSuffix: " HKD",
        summaryLabelPrefix: "小計: ",
        groups: [
          {
            key: "income",
            title: "A) 過去2年的平均每月收入",
            summaryKey: "totalIncome",
            summaryValueSuffix: " HKD",
            summaryLabel: "小計: ",
            gutter: [24, 16],
            questions: [
              {
                key: "salaryCommissionBonus",
                label: "薪金/佣金/花紅",
                kind: "number",
                section: "income",
              },
              {
                key: "rentalIncome",
                label: "租金收入",
                kind: "number",
                section: "income",
              },
              {
                key: "hasOtherIncome",
                label: "是否有其他收入",
                kind: "switch",
                section: "income",
                colSpan: 24,
                variant: "inlineSwitch",
                checkedChildren: <CheckOutlined />,
                unCheckedChildren: <CloseOutlined />,
                options: [
                  { label: "是", value: true },
                  { label: "否", value: false },
                ],
              },
              {
                key: "otherIncomeName",
                label: "其他收入名稱",
                kind: "text",
                section: "income",
                visibleWhen: {
                  predicates: [{ section: "income", key: "hasOtherIncome", truthy: true }],
                },
              },
              {
                key: "otherIncomeAmount",
                label: "其他收入金額",
                kind: "number",
                section: "income",
                visibleWhen: {
                  predicates: [{ section: "income", key: "hasOtherIncome", truthy: true }],
                },
              },
            ],
          },
          {
            key: "expense",
            title: "B) 過去2年的平均每月支出",
            summaryKey: "totalExpense",
            summaryValueSuffix: " HKD",
            summaryLabel: "小計: ",
            gutter: [16, 12],
            questions: [
              {
                key: "personalAndFamilyExpense",
                label: "個人及家庭開支",
                kind: "number",
                section: "expense",
              },
              {
                key: "mortgageLoanRepayment",
                label: "按揭/借貸還款",
                kind: "number",
                section: "expense",
              },
              {
                key: "insuranceContribution",
                label: "保險供款",
                kind: "number",
                section: "expense",
              },
              {
                key: "hasOtherExpense",
                label: "是否有其他支出?",
                kind: "switch",
                section: "expense",
                colSpan: 24,
                variant: "inlineSwitch",
                checkedChildren: <CheckOutlined />,
                unCheckedChildren: <CloseOutlined />,
              },
              {
                key: "otherExpenseName",
                label: "其他支出名稱",
                kind: "text",
                section: "expense",
                visibleWhen: {
                  predicates: [{ section: "expense", key: "hasOtherExpense", truthy: true }],
                },
              },
              {
                key: "otherExpenseAmount",
                label: "其他支出金額",
                kind: "number",
                section: "expense",
                visibleWhen: {
                  predicates: [{ section: "expense", key: "hasOtherExpense", truthy: true }],
                },
              },
            ],
          },
        ],
      },
      {
        key: "assetsLiabilities",
        headerSummaryKey: "netAssets",
        headerLabel: "2. 資產及負債 (HK$/港幣)",
        headerPrefix: "C+D-E = ",
        headerValueSuffix: " HKD",
        summaryLabelPrefix: "小計: ",
        groups: [
          {
            key: "currentAssets",
            title: "C) 流動資產",
            summaryKey: "totalCurrentAssets",
            summaryValueSuffix: " HKD",
            summaryLabel: "小計: ",
            gutter: [16, 12],
            questions: [
              { key: "cashAndDeposits", label: "現金及存款", kind: "number", section: "currentAssets" },
              {
                key: "liquidInvestments",
                label: "變現能力高的投資 (股票/債券等)",
                kind: "number",
                section: "currentAssets",
              },
              {
                key: "hasOtherCurrentAssets",
                label: "是否有其他流動資產?",
                kind: "switch",
                section: "currentAssets",
                colSpan: 24,
                variant: "inlineSwitch",
                checkedChildren: <CheckOutlined />,
                unCheckedChildren: <CloseOutlined />,
              },
              {
                key: "otherCurrentAssetsName",
                label: "其他流動資產名稱",
                kind: "text",
                section: "currentAssets",
                visibleWhen: {
                  predicates: [{ section: "currentAssets", key: "hasOtherCurrentAssets", truthy: true }],
                },
              },
              {
                key: "otherCurrentAssetsAmount",
                label: "其他流動資產金額",
                kind: "number",
                section: "currentAssets",
                visibleWhen: {
                  predicates: [{ section: "currentAssets", key: "hasOtherCurrentAssets", truthy: true }],
                },
              },
            ],
          },
          {
            key: "nonCurrentAssets",
            title: "D) 非流動資產",
            summaryKey: "totalNonCurrentAssets",
            summaryValueSuffix: " HKD",
            summaryLabel: "小計: ",
            gutter: [16, 12],
            questions: [
              { key: "property", label: "物業", kind: "number", section: "nonCurrentAssets" },
              { key: "otherNonCurrentAssets", label: "其他 (如公司股份)", kind: "number", section: "nonCurrentAssets" },
            ],
          },
          {
            key: "liabilities",
            title: "E) 負債",
            summaryKey: "totalLiabilities",
            summaryValueSuffix: " HKD",
            summaryLabel: "小計: ",
            gutter: [16, 12],
            questions: [
              { key: "mortgageLoan", label: "按揭貸款", kind: "number", section: "liabilities" },
              { key: "personalCreditCardLoan", label: "私人/信用卡貸款", kind: "number", section: "liabilities" },
              {
                key: "hasOtherLiabilities",
                label: "是否有其他負債?",
                kind: "switch",
                section: "liabilities",
                colSpan: 24,
                variant: "inlineSwitch",
                checkedChildren: <CheckOutlined />,
                unCheckedChildren: <CloseOutlined />,
              },
              {
                key: "otherLiabilityName",
                label: "其他負債名稱",
                kind: "text",
                section: "liabilities",
                visibleWhen: {
                  predicates: [{ section: "liabilities", key: "hasOtherLiabilities", truthy: true }],
                },
              },
              {
                key: "otherLiabilityAmount",
                label: "其他負債金額",
                kind: "number",
                section: "liabilities",
                visibleWhen: {
                  predicates: [{ section: "liabilities", key: "hasOtherLiabilities", truthy: true }],
                },
              },
            ],
          },
        ],
      },
    ],
  },
  {
    title: "二、現行保障 (HK$/港幣)",
    blocks: [
      {
        key: "protection",
        groups: [
          {
            key: "protectionSummary",
            gutter: [16, 12],
            questions: [
              { key: "lifeInsurance", label: "人壽保險", kind: "text", section: "protection" },
              { key: "criticalIllnessInsurance", label: "危疾保險", kind: "text", section: "protection" },
              {
                key: "medicalInsuranceType",
                label: "醫療保險類別",
                kind: "select",
                section: "protection",
                metadataCode: "MEDICAL_INSURANCE_TYPE",
              },
              { key: "medicalInsuranceAmount", label: "醫療保險金額", kind: "number", section: "protection" },
              { key: "accidentInsurance", label: "意外保險", kind: "text", section: "protection" },
              { key: "otherExistingProtection", label: "其他現有保障", kind: "text", section: "protection" },
              { key: "attachmentRemarks", label: "附件資料/備註", kind: "text", section: "protection" },
            ],
          },
        ],
      },
    ],
  },
];
